import i18next from 'i18next';
import _ from 'lodash';

import {
	DateRange,
	OpportunityStageEnum,
	PeriodData,
	RoleEnum,
	SignalingOpportunityDTO,
	Stats,
} from 'src/api';
import { UserDocument, UserDTO } from 'src/api/models/UserDTO';

export const computeStats = (items: SignalingOpportunityDTO[] = []): Stats => {
	return items.reduce(
		(stats, item) => {
			stats.opportunityCreated += 1;
			stats.opportunityWon +=
				item.StageName === OpportunityStageEnum.closed_won ? 1 : 0;
			stats.provvigioniSegnalatore +=
				item.StageName === OpportunityStageEnum.closed_won
					? item.Provvigioni_Segnalatore__c || 0
					: 0;
			stats.provvigioniDealerVinte +=
				item.StageName === OpportunityStageEnum.closed_won
					? (item.Provvigioni_passive__c || 0) -
					  (item.Provvigioni_Segnalatore__c || 0)
					: 0;
			return stats;
		},
		{
			opportunityCreated: 0,
			opportunityWon: 0,
			provvigioniSegnalatore: 0,
			provvigioniDealerVinte: 0,
		}
	);
};

export const getSlidingDateRange = (
	monthsPerPage: number,
	pageNumber: number
): DateRange => {
	const today = new Date();

	const endDate = new Date(today);
	endDate.setMonth(endDate.getMonth() - monthsPerPage * pageNumber);
	endDate.setUTCHours(23, 59, 59, 999);

	const startDate = new Date(endDate);
	startDate.setMonth(startDate.getMonth() - monthsPerPage);
	startDate.setUTCDate(1);
	startDate.setUTCHours(0, 0, 0, 0);

	return {
		createdDateFrom: startDate.toISOString(),
		createdDateTo: endDate.toISOString(),
	};
};

export const MONTH_NAMES: { [key: number]: string } = {
	0: i18next.t('january'),
	1: i18next.t('february'),
	2: i18next.t('march'),
	3: i18next.t('april'),
	4: i18next.t('may'),
	5: i18next.t('june'),
	6: i18next.t('july'),
	7: i18next.t('august'),
	8: i18next.t('september'),
	9: i18next.t('october'),
	10: i18next.t('november'),
	11: i18next.t('december'),
};

export const formatMonthYear = (dateStr: string): string => {
	const date = new Date(dateStr);
	const month = MONTH_NAMES[date.getMonth()];
	const year = date.getFullYear();
	return `${month} ${year}`;
};

export const groupByMonthYearDesc = (
	records: SignalingOpportunityDTO[]
): PeriodData[] => {
	const grouped: { [key: string]: SignalingOpportunityDTO[] } = {};

	records.forEach((record) => {
		const monthYear = formatMonthYear(record.CreatedDate);
		if (!grouped[monthYear]) grouped[monthYear] = [];
		grouped[monthYear].push(record);
	});

	const sortedKeys = Object.keys(grouped).sort((a, b) => {
		const [monthA, yearA] = a.split(' ');
		const [monthB, yearB] = b.split(' ');

		if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);

		const monthIndexA = Object.values(MONTH_NAMES).indexOf(monthA);
		const monthIndexB = Object.values(MONTH_NAMES).indexOf(monthB);

		return monthIndexB - monthIndexA;
	});

	const sortedPeriods: PeriodData[] = [];

	sortedKeys.forEach((key) => {
		const items = grouped[key];
		const totals = items.reduce(
			(result, item) => {
				result.provvigioniSegnalatore += item.Provvigioni_Segnalatore__c || 0;
				result.provvigioniPassive += item.Provvigioni_passive__c || 0;
				result.provvigioniSegnalatoreVinte +=
					item.StageName === OpportunityStageEnum.closed_won
						? item.Provvigioni_Segnalatore__c || 0
						: 0;
				result.provvigioniDealerVinte +=
					item.StageName === OpportunityStageEnum.closed_won
						? (item.Provvigioni_passive__c || 0) -
						  (item.Provvigioni_Segnalatore__c || 0)
						: 0;
				return result;
			},
			{
				provvigioniSegnalatore: 0,
				provvigioniPassive: 0,
				provvigioniSegnalatoreVinte: 0,
				provvigioniDealerVinte: 0,
			}
		);

		sortedPeriods.push({
			period: key,
			items,
			...totals,
		});
	});

	return sortedPeriods;
};

type SegnalatoreDocument = {
	type: 'file' | 'text';
	code: string;
	label: string;
	templateDocument?: {
		bucket: string;
		filename: string;
	};
};

type SegnalatoreDocumentKey =
	| 'VISURA_CAMERALE_AGGIORNATA'
	| 'CARTA_IDENTITA_SEGNALATORE'
	| 'CODICE_FISCALE_SEGNALATORE'
	| 'CONTRATTO_AGENZIA_O_PROCACCIAMENTO_FIRMATO'
	| 'ACCORDO_INTEGRATIVO_HMP_BRIDGE_DEALER_HORIZON'
	| 'MODULO_SEPA_FIRMATO';

export const SEGNALATORE_REQUIRED_DOCUMENTS: Record<
	SegnalatoreDocumentKey,
	SegnalatoreDocument
> = {
	VISURA_CAMERALE_AGGIORNATA: {
		type: 'file',
		code: 'VISURA_CAMERALE_AGGIORNATA',
		label: i18next.t('visura_camerale_aggiornata'),
	},
	CARTA_IDENTITA_SEGNALATORE: {
		type: 'file',
		code: 'CARTA_IDENTITA_SEGNALATORE',
		label: i18next.t('carta_identita_segnalatore'),
	},
	CODICE_FISCALE_SEGNALATORE: {
		type: 'file',
		code: 'CODICE_FISCALE_SEGNALATORE',
		label: i18next.t('codice_fiscale_segnalatore'),
	},
	CONTRATTO_AGENZIA_O_PROCACCIAMENTO_FIRMATO: {
		type: 'file',
		code: 'CONTRATTO_AGENZIA_O_PROCACCIAMENTO_FIRMATO',
		label: i18next.t('contratto_agenzia_o_procacciamento_firmato'),
		templateDocument: {
			bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_AREA_ACADEMY!,
			filename: 'HORIZON_INCARICO_SEGNALATORE_HMP_BRIDGE_PER_DEALER.pdf',
		}
	},
	ACCORDO_INTEGRATIVO_HMP_BRIDGE_DEALER_HORIZON: {
		type: 'file',
		code: 'ACCORDO_INTEGRATIVO_HMP_BRIDGE_DEALER_HORIZON',
		label: i18next.t('accordo_integrativo_hmp_bridge_dealer_horizon'),
		templateDocument: {
			bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_AREA_ACADEMY!,
			filename: 'HORIZON_FORMAT_ADDENDUM_HMP_BRIDGE.pdf',
		}
	},
	MODULO_SEPA_FIRMATO: {
		type: 'file',
		code: 'MODULO_SEPA_FIRMATO',
		label: i18next.t('modulo_sepa_firmato'),
	},
};

export const requiredDocumentsByRole = (
	userRole: RoleEnum
): Record<SegnalatoreDocumentKey, SegnalatoreDocument> | {} => {
	const isBarem = [
		RoleEnum.MASTER,
		RoleEnum.BACKOFFICE,
		RoleEnum.AREA_MANAGER,
	].includes(userRole);

	const isSalesDealer = [RoleEnum.SALES, RoleEnum.SALES_DEALER].includes(userRole);

	if (isBarem || isSalesDealer) {
		return _.cloneDeep(SEGNALATORE_REQUIRED_DOCUMENTS);
	}

	// if ([RoleEnum.SALES, RoleEnum.SALES_DEALER].includes(userRole)) {
	// 	return _.cloneDeep(
	// 		_.omit(SEGNALATORE_REQUIRED_DOCUMENTS, 'MODULO_SEPA_FIRMATO')
	// 	);
	// }

	return {};
};

export const hasMissingRequiredDocuments = (
	loggedUserRole: RoleEnum,
	body: UserDTO
) => {
	const requiredCodes = Object.values(
		requiredDocumentsByRole(loggedUserRole)
	).map((doc) => doc.code);
	const okCodes = (body.documents || []).map((doc) =>
		doc.fileType?.toUpperCase()
	);

	for (const code of requiredCodes) {
		if (!okCodes.includes(code?.toUpperCase())) {
			return true;
		}
	}

	return false;
};

export const hasMissingPendingDocuments = (
	loggedUserRole: RoleEnum,
	reporterDocuments: {
		isUploaded: boolean;
		fileType: string;
		fileName: string;
		key: string;
		type: 'file' | 'text';
		code: string;
		label: string;
		file?: File | null;
	}[]
) => {
	const requiredCodes = Object.values(
		requiredDocumentsByRole(loggedUserRole)
	).map((doc) => doc.code);

	const okCodes = (reporterDocuments || [])
		.filter((doc) => !!doc.key || !!doc.file)
		.map((doc) => doc.code?.toUpperCase());

	for (const code of requiredCodes) {
		if (!okCodes.includes(code?.toUpperCase())) {
			return true;
		}
	}

	return false;
};
